import React, { useCallback, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconCamera, IconPhoto } from '@tabler/icons-react'
import { SANS_FONT, SERIF_FONT } from '../theme'
import { Capacitor } from '@capacitor/core'
import { Camera } from '@capacitor/camera'
import { logEvent } from '../analytics/analytics'
import { emitCameraPromptIfNeeded } from '../helpers/camera-analytics'
import { errorLog } from '../utils/log-helper'
import type { MenuScanOpenedParams } from '../analytics/events'

type EntryPoint = MenuScanOpenedParams['entry_point']

/**
 * Pre-capture coaching for the menu-scan flow.
 *
 * 100% of scans historically returned no dishes because users tapped "scan" and the camera opened
 * with no cue that it was for a MENU — so they photographed random things. This sheet shows a quick,
 * on-brand cue to point the camera at the restaurant's menu BEFORE any capture UI opens. It is purely
 * additive: confirming proceeds to the exact same getPhoto / file-input path the site used before.
 */
/**
 * App Store Guideline 5.1.1 — on NATIVE this sheet must never sit between the user and the camera
 * permission request. Apple rejected the build for exactly that: a custom message preceded the OS
 * prompt, its primary button said "Take photo", and "Not now" let the user dismiss and delay the
 * request. useMenuScanCoaching below now resolves camera permission FIRST on native, so by the time
 * this sheet renders the OS prompt has already been answered and the sheet is pure framing guidance.
 * On native the primary action therefore reads "Continue" and there is no dismissal path.
 */
export interface MenuScanCoachingSheetProps {
  open: boolean
  onClose: () => void
  /** Proceed to the existing capture path (getPhoto on native, file input on web). */
  onConfirm: () => void
  /** Native: "Continue", no dismissal. Web: "Choose photo" + "Not now" (no OS permission gate). */
  isNative: boolean
}

const HINTS = [
  'Hold the camera straight on, so the menu fills the frame',
  'Make sure the text is in focus and well lit',
  'One page at a time works better than the whole table',
]

const MenuScanCoachingSheet: React.FC<MenuScanCoachingSheetProps> = ({
  open,
  onClose,
  onConfirm,
  isNative,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      open={open}
      // Native: no backdrop/escape dismissal — the sheet always leads on to capture (see 5.1.1 note
      // above). Web keeps the normal dismissal affordances; there is no OS permission gate there.
      onClose={isNative ? undefined : onClose}
      disableEscapeKeyDown={isNative}
      aria-labelledby="menu-scan-coaching-title"
      fullWidth
      maxWidth="xs"
      sx={{
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-end' : 'center',
        },
      }}
      PaperProps={{
        sx: {
          m: isMobile ? 0 : 2,
          width: '100%',
          borderRadius: isMobile ? '20px 20px 0 0' : '20px',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ p: { xs: '24px 20px', sm: '28px 24px' }, textAlign: 'center' }}>
        {/* Icon badge — same visual language as the scan banner */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            mx: 'auto',
            mb: '16px',
            borderRadius: '18px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <IconCamera size={32} color="white" strokeWidth={1.8} />
        </Box>

        <Typography
          id="menu-scan-coaching-title"
          component="h2"
          sx={{
            fontSize: { xs: '19px', sm: '20px' },
            fontWeight: 700,
            lineHeight: 1.3,
            color: '#1F2937',
            mb: '6px',
            fontFamily: SERIF_FONT,
          }}
        >
          Snap a photo of the menu
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '15px' },
            fontWeight: 500,
            lineHeight: 1.45,
            color: '#6B7580',
            mb: '18px',
            fontFamily: SANS_FONT,
          }}
        >
          Point your camera at the restaurant&apos;s menu and we&apos;ll pull out the dishes for you.
        </Typography>

        {/* Framing hints */}
        <Box
          component="ul"
          sx={{
            textAlign: 'left',
            listStyle: 'none',
            m: 0,
            mb: '22px',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {HINTS.map((hint) => (
            <Box
              component="li"
              key={hint}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  mt: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: alpha(theme.palette.primary.main, 0.12),
                }}
              >
                <IconPhoto size={13} color={theme.palette.primary.main} strokeWidth={2} />
              </Box>
              <Typography
                sx={{
                  fontSize: '13.5px',
                  lineHeight: 1.4,
                  color: '#4B5563',
                  fontFamily: SANS_FONT,
                }}
              >
                {hint}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={onConfirm}
          startIcon={<IconCamera size={20} stroke={2} />}
          sx={{
            minHeight: '50px',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '16px',
            color: 'white',
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
            },
          }}
        >
          {isNative ? 'Continue' : 'Choose photo'}
        </Button>
        {/* Web only. On native a "Not now" here would read as delaying a permission request. */}
        {!isNative && (
          <Button
            fullWidth
            variant="text"
            onClick={onClose}
            sx={{
              mt: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              color: '#6B7580',
            }}
          >
            Not now
          </Button>
        )}
      </Box>
    </Dialog>
  )
}

export default MenuScanCoachingSheet

/**
 * Controller for the pre-capture coaching sheet. Keeps all three launch sites (nav FAB, scan banner,
 * results retry) sharing one implementation.
 *
 * Usage:
 *   const { requestCapture, coachingProps } = useMenuScanCoaching('nav_bar', 'nav')
 *   // on scan tap, after logging menu_scan_opened:
 *   requestCapture(() => { ...existing getPhoto / file-input code... })
 *   // render once:
 *   <MenuScanCoachingSheet {...coachingProps} />
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useMenuScanCoaching(screenName: string, entryPoint?: EntryPoint) {
  const [open, setOpen] = useState(false)
  const proceedRef = useRef<(() => void) | null>(null)
  const isNative = Capacitor.isNativePlatform()

  const showSheet = useCallback((): void => {
    setOpen(true)
    logEvent('menu_scan_coaching_shown', {
      screen_name: screenName,
      entry_point: entryPoint,
    })
  }, [screenName, entryPoint])

  const requestCapture = useCallback(
    (proceed: () => void): void => {
      proceedRef.current = proceed

      // Web has no OS permission gate (the capture path is a file input), so show the cue directly.
      if (!isNative) {
        showSheet()
        return
      }

      // Native (App Store 5.1.1): settle camera permission BEFORE showing any custom message.
      void (async () => {
        try {
          let { camera } = await Camera.checkPermissions()

          if (camera === 'prompt' || camera === 'prompt-with-rationale') {
            // Emit the funnel step while the state is still "prompt" — the call sites' own
            // emitCameraPromptIfNeeded no-ops after this, so there is no double count.
            await emitCameraPromptIfNeeded(screenName)
            const result = await Camera.requestPermissions({ permissions: ['camera'] })
            camera = result.camera
          }

          if (camera === 'denied') {
            // Nothing to coach — hand straight off so the call site's existing denial handling
            // (Settings guidance / camera_permission=denied) runs unchanged.
            const proceed = proceedRef.current
            proceedRef.current = null
            proceed?.()
            return
          }

          showSheet()
        } catch (e) {
          // Never strand the user on a permission-plumbing failure — fall through to capture.
          errorLog('[menu-scan-coaching] camera permission check failed', e)
          const proceed = proceedRef.current
          proceedRef.current = null
          proceed?.()
        }
      })()
    },
    [isNative, screenName, showSheet],
  )

  const handleConfirm = useCallback((): void => {
    setOpen(false)
    const proceed = proceedRef.current
    proceedRef.current = null
    // Runs inside the button-click gesture, so native getPhoto / permission prompts still work.
    proceed?.()
  }, [])

  const handleClose = useCallback((): void => {
    setOpen(false)
    // Only an abandonment if the user backed out before proceeding to capture (proceedRef is
    // cleared by handleConfirm). Pairs with menu_scan_coaching_shown to measure drop-off here.
    if (proceedRef.current) {
      logEvent('menu_scan_coaching_dismissed', {
        screen_name: screenName,
        entry_point: entryPoint,
      })
    }
    proceedRef.current = null
  }, [screenName, entryPoint])

  const coachingProps: MenuScanCoachingSheetProps = {
    open,
    onClose: handleClose,
    onConfirm: handleConfirm,
    isNative,
  }

  return { requestCapture, coachingProps }
}
